export interface ParallelTrack {
  name: string;
  next_steps: string[];
  convergence: string | null;
  has_separate_ending?: boolean;
  ending_type?: 'success' | 'failure' | 'rejection' | 'alternative';
}

export interface NoPathDetails {
  steps: string[];
  convergence: string | null;
  has_separate_ending?: boolean;
}

export interface DecisionPaths {
  yes?: string;
  no?: string;
  yes_condition?: string;
  no_condition?: string;
  default_path?: string;
  parallel_tracks?: {
    track_A?: ParallelTrack;
    track_B?: ParallelTrack;
  };
  no_path_details?: NoPathDetails;
}

export interface SequenceFlow {
  id: string;
  source_step: string;
  target_step: string;
  condition: string | null;
  condition_expression: string | null;
  is_default: boolean;
}

export interface DataObject {
  id: string;
  name: string;
  type: 'input' | 'output';
}

export interface BoundaryEvent {
  id: string;
  event_type: 'timerEvent' | 'errorEvent' | 'messageEvent' | 'escalationEvent';
  name: string;
  timer_definition?: string;
  error_code?: string;
  target_step: string;
}

export type BpmnType = 'event' | 'task' | 'gateway';
export type EventType = 'startEvent' | 'endEvent' | 'timerEvent' | 'messageEvent' | 'errorEvent' | null;
export type TaskType = 'userTask' | 'serviceTask' | 'sendTask' | 'receiveTask' | 'manualTask' | 'scriptTask' | null;
export type GatewayType = 'exclusive' | 'inclusive' | 'parallel' | null;

export interface ProcessStep {
  id?: string;
  name: string;
  step?: string;
  fullName?: string;
  type?: 'start' | 'process' | 'end' | 'decision' | 'split' | 'merge' | 'gateway';
  category?: string;
  description?: string;
  assignee?: string;
  step_index?: number;
  step_number?: string;
  action_type?: string;
  bpmn_type?: BpmnType;
  event_type?: EventType;
  task_type?: TaskType;
  gateway_type?: GatewayType;
  decision_paths?: DecisionPaths;
  decisionPaths?: DecisionPaths;
  decision_criteria?: string;
  confidence_score?: number;
  outgoing_flows?: string[];
  incoming_flows?: string[];
  boundary_events?: BoundaryEvent[];
  data_objects?: DataObject[];
  timer_definition?: string | null;
  documentation?: string;
  parent_l3_step?: string;
  parent_l3_index?: number;
  is_subprocess_candidate?: boolean;
  subprocess_name?: string;
  is_high_level_task?: boolean;
  expansion_hint?: string;
}

export interface ExtendedProcessStep extends ProcessStep {
  id: string;
  name: string;
  position: { x: number; y: number };
  swimlane: string;
  connections: string[];
  decisionPaths?: DecisionPaths;
  sources: any;
  globalPosition: number;
  full_text: string;
  assignee: string;
  description: string;
  type: ProcessStepType;
  category: string;
  hybrid_description?: string;
  actor?: string;
  actor_note?: string;
  systems_context?: string;
  systems_normalized?: boolean;
  duration?: string;
  atomicity_validated?: boolean;
  source_text?: string;
  step_name?: string;
  phase?: string;
  detailed_description?: string;
  systems_involved?: string[];
  business_criteria?: string | string[];
  inputs?: string[];
  outputs?: string[];
  source_citations?: string[];
  parent_l3_step?: string;
  parent_l3_index?: number;
  is_subprocess_candidate?: boolean;
  subprocess_name?: string;
  is_high_level_task?: boolean;
  expansion_hint?: string;
}

export type ProcessStepType = 'start' | 'process' | 'end' | 'decision' | 'split' | 'merge' | 'gateway';
export type ExtendedProcessStepType = ProcessStepType | 'approval' | 'communication' | 'documentation' | 'validation';

export const BPMN_ELEMENT_COLORS: { [key: string]: string } = {
  'startEvent': '#4CAF50',
  'endEvent': '#F44336',
  'userTask': '#2196F3',
  'serviceTask': '#9C27B0',
  'sendTask': '#00BCD4',
  'receiveTask': '#FF9800',
  'manualTask': '#795548',
  'scriptTask': '#607D8B',
  'exclusiveGateway': '#FF9800',
  'inclusiveGateway': '#FFC107',
  'parallelGateway': '#E91E63',
  'timerEvent': '#FF5722',
  'messageEvent': '#00BCD4',
  'errorEvent': '#F44336'
};

export const PROCESS_TYPE_COLORS: { [key: string]: string } = {
  'Start Points': '#4CAF50',
  'End Points': '#F44336',
  'Action Steps': '#2196F3',
  'Decision Points': '#FF9800',
  'Split Points': '#9C27B0',
  'Gateway Points': '#E91E63',
  'Merge Points': '#E91E63',
  'Notifications': '#00BCD4',
  'Testing/Quality': '#FF5722',
  'Development': '#607D8B',
  'Documentation': '#795548',
  'Communication': '#00BCD4',
  'Validation': '#FF9800',
  'Approval': '#FF9800',
  'Review': '#FF9800',
  'User Tasks': '#2196F3',
  'Service Tasks': '#9C27B0',
  'Send Tasks': '#00BCD4',
  'Receive Tasks': '#FF9800',
  'Manual Tasks': '#795548',
  'Script Tasks': '#607D8B'
};

export const CATEGORY_ALIASES: { [key: string]: string } = {
  'Start': 'Start Points',
  'End': 'End Points',
  'Action': 'Action Steps',
  'Decision': 'Decision Points',
  'Split': 'Split Points',
  'Gateway': 'Gateway Points',
  'Merge': 'Merge Points',
  'Notification': 'Notifications',
  'Testing': 'Testing/Quality',
  'Communication': 'Notifications',
  'Quality Assurance': 'Testing/Quality',
  'userTask': 'User Tasks',
  'serviceTask': 'Service Tasks',
  'sendTask': 'Send Tasks',
  'receiveTask': 'Receive Tasks',
  'manualTask': 'Manual Tasks',
  'scriptTask': 'Script Tasks'
};

export const GATEWAY_TYPE_LABELS: { [key: string]: string } = {
  'exclusive': 'Exclusive (XOR)',
  'inclusive': 'Inclusive (OR)',
  'parallel': 'Parallel (AND)'
};

export const TASK_TYPE_LABELS: { [key: string]: string } = {
  'userTask': 'User Task',
  'serviceTask': 'Service Task',
  'sendTask': 'Send Task',
  'receiveTask': 'Receive Task',
  'manualTask': 'Manual Task',
  'scriptTask': 'Script Task'
};

export const EVENT_TYPE_LABELS: { [key: string]: string } = {
  'startEvent': 'Start Event',
  'endEvent': 'End Event',
  'timerEvent': 'Timer Event',
  'messageEvent': 'Message Event',
  'errorEvent': 'Error Event'
};

export const getBpmnElementColor = (step: ProcessStep): string => {
  if (step.bpmn_type === 'event' && step.event_type) {
    return BPMN_ELEMENT_COLORS[step.event_type] || '#95A5A6';
  }
  if (step.bpmn_type === 'gateway' && step.gateway_type) {
    return BPMN_ELEMENT_COLORS[`${step.gateway_type}Gateway`] || '#FF9800';
  }
  if (step.bpmn_type === 'task' && step.task_type) {
    return BPMN_ELEMENT_COLORS[step.task_type] || '#2196F3';
  }
  return '#95A5A6';
};

export const getBpmnTypeLabel = (step: ProcessStep): string => {
  if (step.bpmn_type === 'event' && step.event_type) {
    return EVENT_TYPE_LABELS[step.event_type] || step.event_type;
  }
  if (step.bpmn_type === 'gateway' && step.gateway_type) {
    return GATEWAY_TYPE_LABELS[step.gateway_type] || step.gateway_type;
  }
  if (step.bpmn_type === 'task' && step.task_type) {
    return TASK_TYPE_LABELS[step.task_type] || step.task_type;
  }
  return 'Unknown';
};

export const inferBpmnType = (step: ProcessStep): BpmnType => {
  const actionType = (step.action_type || '').toLowerCase();
  if (actionType === 'start' || actionType === 'end') {
    return 'event';
  }
  if (actionType === 'decision' || actionType === 'split' || actionType === 'merge' || actionType === 'gateway') {
    return 'gateway';
  }
  return 'task';
};

export const inferEventType = (step: ProcessStep): EventType => {
  const actionType = (step.action_type || '').toLowerCase();
  if (actionType === 'start') return 'startEvent';
  if (actionType === 'end') return 'endEvent';
  return null;
};

export const inferTaskType = (step: ProcessStep): TaskType => {
  if (step.bpmn_type === 'event' || step.bpmn_type === 'gateway') {
    return null;
  }
  const actionType = (step.action_type || '').toLowerCase();
  const stepName = (step.name || '').toLowerCase();
  const description = (step.description || '').toLowerCase();
  if (actionType === 'communication' || stepName.includes('send') || stepName.includes('email') || stepName.includes('notify')) {
    return 'sendTask';
  }
  if (stepName.includes('wait') || stepName.includes('receive') || stepName.includes('await')) {
    return 'receiveTask';
  }
  if (stepName.includes('auto') || stepName.includes('calculate') || stepName.includes('generate') || description.includes('system')) {
    return 'serviceTask';
  }
  if (stepName.includes('file') || stepName.includes('physical') || stepName.includes('manual')) {
    return 'manualTask';
  }
  if (stepName.includes('batch') || stepName.includes('script') || stepName.includes('job')) {
    return 'scriptTask';
  }
  return 'userTask';
};

export const inferGatewayType = (step: ProcessStep): GatewayType => {
  if (step.bpmn_type !== 'gateway') {
    return null;
  }
  const actionType = (step.action_type || '').toLowerCase();
  if (actionType === 'split' || actionType === 'merge') {
    return 'parallel';
  }
  const decisionPaths = step.decision_paths || step.decisionPaths;
  if (decisionPaths?.parallel_tracks) {
    return 'parallel';
  }
  return 'exclusive';
};

export const categorizeProcessStep = (
  step: ProcessStep | ExtendedProcessStep | any,
  index: number,
  totalSteps: number
): { type: ProcessStepType; category: string; bpmn_type: BpmnType; task_type: TaskType; gateway_type: GatewayType; event_type: EventType } => {
  const bpmnType = step.bpmn_type || inferBpmnType(step);
  const eventType = step.event_type || inferEventType(step);
  const taskType = step.task_type || inferTaskType(step);
  const gatewayType = step.gateway_type || inferGatewayType(step);

  if (step.action_type) {
    const actionType = step.action_type.toLowerCase();

    switch (actionType) {
      case 'decision':
      case 'approval':
      case 'review':
      case 'validation':
        return {
          type: 'decision',
          category: 'Decision Points',
          bpmn_type: 'gateway',
          task_type: null,
          gateway_type: gatewayType || 'exclusive',
          event_type: null
        };

      case 'split':
        return {
          type: 'split',
          category: 'Split Points',
          bpmn_type: 'gateway',
          task_type: null,
          gateway_type: 'parallel',
          event_type: null
        };

      case 'gateway':
      case 'merge':
        return {
          type: 'merge',
          category: 'Merge Points',
          bpmn_type: 'gateway',
          task_type: null,
          gateway_type: 'parallel',
          event_type: null
        };

      case 'communication':
      case 'notification':
        return {
          type: 'process',
          category: 'Notifications',
          bpmn_type: 'task',
          task_type: 'sendTask',
          gateway_type: null,
          event_type: null
        };

      case 'documentation':
        return {
          type: 'process',
          category: 'Documentation',
          bpmn_type: 'task',
          task_type: 'userTask',
          gateway_type: null,
          event_type: null
        };

      case 'testing':
      case 'quality':
        return {
          type: 'process',
          category: 'Testing/Quality',
          bpmn_type: 'task',
          task_type: 'userTask',
          gateway_type: null,
          event_type: null
        };

      case 'development':
      case 'creation':
        return {
          type: 'process',
          category: 'Development',
          bpmn_type: 'task',
          task_type: 'userTask',
          gateway_type: null,
          event_type: null
        };

      case 'start':
        return {
          type: 'start',
          category: 'Start Points',
          bpmn_type: 'event',
          task_type: null,
          gateway_type: null,
          event_type: 'startEvent'
        };

      case 'end':
        return {
          type: 'end',
          category: 'End Points',
          bpmn_type: 'event',
          task_type: null,
          gateway_type: null,
          event_type: 'endEvent'
        };

      case 'task':
      case 'process':
      default:
        if (index === 0) {
          return {
            type: 'start',
            category: 'Start Points',
            bpmn_type: 'event',
            task_type: null,
            gateway_type: null,
            event_type: 'startEvent'
          };
        }
        const stepName = (step.step_name || step.name || '').toLowerCase();
        if (index === totalSteps - 1 || stepName.startsWith('end')) {
          return {
            type: 'end',
            category: 'End Points',
            bpmn_type: 'event',
            task_type: null,
            gateway_type: null,
            event_type: 'endEvent'
          };
        }
        return {
          type: 'process',
          category: taskType ? CATEGORY_ALIASES[taskType] || 'Action Steps' : 'Action Steps',
          bpmn_type: 'task',
          task_type: taskType || 'userTask',
          gateway_type: null,
          event_type: null
        };
    }
  }

  if (index === 0) {
    return {
      type: 'start',
      category: 'Start Points',
      bpmn_type: 'event',
      task_type: null,
      gateway_type: null,
      event_type: 'startEvent'
    };
  }
  const stepNameFallback = (step.step_name || step.name || '').toLowerCase();
  if (index === totalSteps - 1 || stepNameFallback.startsWith('end')) {
    return {
      type: 'end',
      category: 'End Points',
      bpmn_type: 'event',
      task_type: null,
      gateway_type: null,
      event_type: 'endEvent'
    };
  }
  return {
    type: 'process',
    category: 'Action Steps',
    bpmn_type: 'task',
    task_type: 'userTask',
    gateway_type: null,
    event_type: null
  };
};

export const getCategoryOnly = (step: ProcessStep, index: number, totalSteps: number): string => {
  return categorizeProcessStep(step, index, totalSteps).category;
};

export const getTypeOnly = (step: ProcessStep, index: number, totalSteps: number): ProcessStepType => {
  return categorizeProcessStep(step, index, totalSteps).type;
};

export const categorizeProcessSteps = (steps: ProcessStep[]): ProcessStep[] => {
  return steps.map((step, index) => {
    const result = categorizeProcessStep(step, index, steps.length);
    return {
      ...step,
      type: result.type,
      category: result.category,
      bpmn_type: result.bpmn_type,
      task_type: result.task_type,
      gateway_type: result.gateway_type,
      event_type: result.event_type
    };
  });
};

export const calculateCategoryCount = (steps: ProcessStep[]): Record<string, number> => {
  const counts: Record<string, number> = {};
  steps.forEach((step, index) => {
    const category = getCategoryOnly(step, index, steps.length);
    counts[category] = (counts[category] || 0) + 1;
  });
  return counts;
};

export const calculateBpmnTypeCount = (steps: ProcessStep[]): { events: number; tasks: number; gateways: number } => {
  let events = 0;
  let tasks = 0;
  let gateways = 0;
  steps.forEach((step, index) => {
    const result = categorizeProcessStep(step, index, steps.length);
    if (result.bpmn_type === 'event') events++;
    else if (result.bpmn_type === 'task') tasks++;
    else if (result.bpmn_type === 'gateway') gateways++;
  });
  return { events, tasks, gateways };
};

export const generateChartData = (categoryCount: Record<string, number>, totalSteps: number) => {
  return Object.entries(categoryCount)
    .map(([category, count]) => {
      const percentage = Math.round((count / totalSteps) * 100);
      return {
        name: category,
        value: count,
        percentage: percentage,
        label: `${category}: ${count} steps (${percentage}%)`,
        color: PROCESS_TYPE_COLORS[category] || '#95A5A6'
      };
    })
    .filter(item => item.value > 0)
    .sort((a, b) => b.value - a.value);
};

export const getCategoryColor = (category: string): string => {
  return PROCESS_TYPE_COLORS[category] || PROCESS_TYPE_COLORS[CATEGORY_ALIASES[category]] || '#95A5A6';
};

export const validateCategorization = (steps: ProcessStep[]): {
  isValid: boolean;
  issues: string[];
  suggestions: string[];
  stats: { [key: string]: number };
  bpmnStats: { events: number; tasks: number; gateways: number };
} => {
  const issues: string[] = [];
  const suggestions: string[] = [];

  if (steps.length === 0) {
    return {
      isValid: false,
      issues: ['No steps provided'],
      suggestions: [],
      stats: {},
      bpmnStats: { events: 0, tasks: 0, gateways: 0 }
    };
  }

  const categoryCount = calculateCategoryCount(steps);
  const bpmnStats = calculateBpmnTypeCount(steps);
  const categories = Object.keys(categoryCount);

  if (categories.length === 1 && categories[0] === 'Action Steps') {
    issues.push('All steps categorized as Action Steps - may indicate poor step naming');
    suggestions.push('Review step names for more specific keywords (test, review, notify, etc.)');
  }

  if (steps.length > 3) {
    if (bpmnStats.events < 2) {
      suggestions.push('BPMN processes should have start and end events');
    }
  }

  if (steps.length > 5 && bpmnStats.gateways === 0) {
    suggestions.push('Complex processes usually have decision gateways');
  }

  const hasStartEvent = steps.some(s => s.action_type === 'start' || s.event_type === 'startEvent');
  const hasEndEvent = steps.some(s => s.action_type === 'end' || s.event_type === 'endEvent');

  if (!hasStartEvent) {
    issues.push('Missing start event - BPMN processes must have a start event');
  }
  if (!hasEndEvent) {
    issues.push('Missing end event - BPMN processes must have an end event');
  }

  return {
    isValid: issues.length === 0,
    issues,
    suggestions,
    stats: categoryCount,
    bpmnStats
  };
};

export const categorizeProcess = (processName: string, description?: string): string => {
  const mockStep: ProcessStep = {
    name: processName,
    description: description || ''
  };
  const result = categorizeProcessStep(mockStep, 0, 1);
  return result.category;
};

export const categorizeStep = (stepName: string, description?: string): string => {
  const mockStep: ProcessStep = {
    name: stepName,
    description: description || ''
  };
  const result = categorizeProcessStep(mockStep, 0, 1);
  return result.category;
};

export const mapLegacyCategory = (oldCategory: string): string => {
  return CATEGORY_ALIASES[oldCategory] || oldCategory;
};

export const getGatewaySymbol = (gatewayType: GatewayType): string => {
  switch (gatewayType) {
    case 'exclusive': return '✕';  // Bold X for exclusive gateway
    case 'inclusive': return '◉';  // Filled circle for inclusive gateway
    case 'parallel': return '✚';   // Bold plus for parallel gateway
    default: return '◆';           // Filled diamond for default gateway
  }
};

export const getEventSymbol = (eventType: EventType): string => {
  switch (eventType) {
    case 'startEvent': return '▶';
    case 'endEvent': return '■';
    case 'timerEvent': return '⏱';
    case 'messageEvent': return '✉';
    case 'errorEvent': return '⚠';
    default: return '○';
  }
};

export const getTaskSymbol = (taskType: TaskType): string => {
  switch (taskType) {
    case 'userTask': return '👤';
    case 'serviceTask': return '⚙';
    case 'sendTask': return '📤';
    case 'receiveTask': return '📥';
    case 'manualTask': return '✋';
    case 'scriptTask': return '📜';
    default: return '□';
  }
};
